import { Flex, Spacer, Text, VStack } from '@chakra-ui/react'
import { Pencil, Plus, X } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useModalState } from '~/atoms/ModalState'
import { CallMode, Ringtone, useSettingsState } from '~/atoms/SettingsState'
import { useLastOutboundPhoneNumber } from '~/atoms/WebPhoneInteractionState'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { TextButtonVariants } from '~/components/shared/componentStyles/TextButton.style'
import { DropdownSelect } from '~/components/shared/Dropdown/DropdownSelect'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { TextButton } from '~/components/shared/TextButton'
import { Size } from '~/types/ThemeTypes'
import { SpeedDial } from '~/types/WebphoneTypes'

import { usePhoneSettingsStyles } from './componentStyles/PhoneSettings.styles'
import { PhoneSettingToggleDependentSection } from './PhoneSettingItems'
import { SettingToggle } from './SettingItems'
import {
  DefaultSettingsWrapper,
  DropdownWrapper,
  SubheadingSection,
} from './Shared'

interface PhoneSettingsProps {
  pbxId: number | undefined
}

const MAX_SPEED_DIALS = 50

export const PhoneSettings = ({ pbxId }: PhoneSettingsProps) => {
  const {
    settings: {
      webPhone: {
        ringtone: defaultRingtone,
        hasCallWaiting,
        allowClickToCallIncoming,
        enableCallerIdSelection,
      },
      clickToCall: { clickToCallMode, shouldAutoAnswer },
      pbx: { parkingLots, extensions },
      speedDial,
    },
    canUseWebPhone,
    removeParkingLot,
    removeTrackedExtension,
    setRingtone,
    setHasCallWaiting,
    setAllowClickToCallIncoming,
    setClickToCallMode,
    setShouldAutoAnswer,
    setEnableCallerIdSelection,
    removeSpeedDial,
  } = useSettingsState(pbxId)
  const { t } = useTranslation()
  const [, setLastUsedOutboundPhone] = useLastOutboundPhoneNumber()
  const styles = usePhoneSettingsStyles()

  const {
    parkingModal: [, setParkingModalState],
    extensionModal: [, setExtensionModalState],
    speedDialModal: [, setSpeedDialModalState],
  } = useModalState()

  const CallModeMap = new Map<CallMode, string>([
    [CallMode.CLICK_TO_CALL, t('phoneSettings.sendToDeskPhone')],
  ])
  if (canUseWebPhone) {
    CallModeMap.set(CallMode.WEBPHONE, t('phoneSettings.useWebPhone'))
  }
  const handleSilenceRingtoneToggle = () => {
    if (defaultRingtone === Ringtone.DEFAULT) {
      setRingtone(Ringtone.SILENT)
    } else {
      setRingtone(Ringtone.DEFAULT)
    }
  }
  const handleCallWaitingToggle = () => {
    setHasCallWaiting(!hasCallWaiting)
  }
  const handleAllowClickToCallIncomingToggle = () => {
    setAllowClickToCallIncoming(!allowClickToCallIncoming)
  }

  const handleChangeCallModeSelection = (callMode: CallMode) => {
    setClickToCallMode(callMode)
  }
  const handleAutoAnswerToggle = () => {
    setShouldAutoAnswer(!shouldAutoAnswer)
  }

  const handleRemoveParkingLot = (id: number) => {
    removeParkingLot(id)
  }
  const handleAddParkingLot = () => {
    setParkingModalState({ open: true })
  }

  const handleRemoveExtension = (id: number) => {
    removeTrackedExtension(id)
  }
  const handleAddExtension = () => {
    setExtensionModalState({ open: true })
  }

  const handleEnableCallerIdSelection = () => {
    if (enableCallerIdSelection) {
      setLastUsedOutboundPhone(null)
    }
    setEnableCallerIdSelection(!enableCallerIdSelection)
  }

  const handleAddSpeedDial = () => {
    setSpeedDialModalState({ open: true })
  }
  const handleEditSpeedDial = (speedDial: SpeedDial) => {
    setSpeedDialModalState({ open: true, speedDial })
  }

  const clickToCallDropdownOptions = Array.from(
    CallModeMap,
    ([key, content]) => ({
      value: key,
      content: content,
    }),
  )

  const isRingtoneActive = defaultRingtone !== Ringtone.SILENT
  const shouldShowClickToCallDropdown = clickToCallDropdownOptions.length > 1
  const shouldShowAutoAnswer = clickToCallMode === CallMode.CLICK_TO_CALL

  return (
    <DefaultSettingsWrapper>
      <PhoneSettingToggleDependentSection
        isChecked={canUseWebPhone}
        toggleOnContent={
          <VStack css={styles.vStack1} gap="16px">
            <SubheadingSection id="incoming-calls">
              <Text css={styles.text1} color="fg.default">
                {t('phoneSettings.incomingCalls')}
              </Text>
              <SettingToggle
                label={t('phoneSettings.showInboundC2CCalls')}
                isChecked={allowClickToCallIncoming}
                onClick={handleAllowClickToCallIncomingToggle}
              />
              <SettingToggle
                label={t('phoneSettings.playRingtone')}
                isChecked={isRingtoneActive}
                onClick={handleSilenceRingtoneToggle}
              />
              <SettingToggle
                label={t('phoneSettings.respondWithBusyToSecondCall')}
                isChecked={hasCallWaiting ?? true}
                onClick={handleCallWaitingToggle}
              />
            </SubheadingSection>
            <SubheadingSection id="outgoing-calls">
              <Text css={styles.text1} color="fg.default">
                {t('phoneSettings.outgoingCalls')}
              </Text>

              {shouldShowClickToCallDropdown && (
                <DropdownWrapper>
                  <Text css={styles.text2} color="fg.muted">
                    {t('phoneSettings.defaultOutboundPhone')}
                  </Text>
                  <DropdownSelect
                    items={clickToCallDropdownOptions}
                    selectedValue={clickToCallMode}
                    onSelect={handleChangeCallModeSelection}
                  />
                </DropdownWrapper>
              )}
              {shouldShowAutoAnswer && (
                <SettingToggle
                  label={t('phoneSettings.autoAnswer')}
                  isChecked={shouldAutoAnswer}
                  onClick={handleAutoAnswerToggle}
                />
              )}
              <Text id="caller-id" textStyle="Body2_Bold">
                {t('phoneSettings.callerId')}
              </Text>
              <SettingToggle
                label={t('phoneSettings.enableCallerIdSelection')}
                subLabel={t('phoneSettings.enableCallerIdSelectionDescription')}
                isChecked={enableCallerIdSelection}
                onClick={handleEnableCallerIdSelection}
              />
            </SubheadingSection>
            <SubheadingSection id="presence">
              <Text css={styles.text1} color="fg.default">
                {t('phoneSettings.presence')}
              </Text>
              <Text css={styles.text2} color="fg.muted">
                {t('phoneSettings.presenceDescription')}
              </Text>
              <Flex
                id="parking-spots"
                width={'100%'}
                borderRadius={'0.25rem'}
                display={'flex'}
                flexDir={'column'}
                marginTop={'0.5rem'}
                alignItems={'flex-start'}
                gap={'0.25rem'}
              >
                <Text css={styles.text3} color="fg.default">
                  {t('phoneSettings.parkingSpotsDisplayed')}
                </Text>
                {parkingLots?.map((lot) => {
                  return (
                    <Flex key={lot.id} css={styles.parkingContainer}>
                      <Text css={styles.text2} color="fg.default">
                        {lot.label}
                      </Text>
                      {lot.label !== lot.defaultLabel && (
                        <Text css={styles.text4} color="fg.default">
                          ({lot.defaultLabel})
                        </Text>
                      )}
                      <Spacer />
                      <QuickActionButton
                        variant={QuickActionButtonVariants.Default_100}
                        size={Size.sm}
                        actionIcon={X}
                        tooltipText={t('phoneSettings.removeParkingSpot')}
                        onClick={() => handleRemoveParkingLot(lot.id)}
                        iconProps={{ boxSize: '12px', color: 'fg.subtle' }}
                      />
                    </Flex>
                  )
                })}
                {(parkingLots?.length ?? 0) < 5 && (
                  <TextButton
                    leftIcon={Plus}
                    onLinkClick={handleAddParkingLot}
                    variant={TextButtonVariants.Info}
                    textButtonProps={{
                      padding: '.5rem',
                      color: 'fg.info',
                    }}
                    leftIconProps={{
                      boxSize: '12px',
                      position: 'relative',
                      bottom: '.5px',
                      stroke: 'inherit',
                      strokeWidth: '2px',
                    }}
                  >
                    {t('phoneSettings.addParkingSpot')}
                  </TextButton>
                )}
              </Flex>

              <Flex
                id="extensions"
                width={'100%'}
                borderRadius={'0.25rem'}
                display={'flex'}
                flexDir={'column'}
                marginTop={'0.5rem'}
                alignItems={'flex-start'}
                gap={'0.25rem'}
              >
                <Text css={styles.text3} color="fg.default">
                  {t('phoneSettings.lineExtensionsDisplayed')}
                </Text>
                {extensions?.map((ext) => {
                  return (
                    <Flex key={ext.id} css={styles.extensionsContainer}>
                      <Text css={styles.text2} color="fg.default">
                        {ext.name}
                      </Text>
                      <Spacer />
                      <QuickActionButton
                        variant={QuickActionButtonVariants.Default_100}
                        size={Size.sm}
                        actionIcon={X}
                        tooltipText={t('phoneSettings.removeExtension')}
                        onClick={() => handleRemoveExtension(ext.id)}
                        iconProps={{ boxSize: '0.75rem' }}
                      />
                    </Flex>
                  )
                })}
                {(extensions?.length ?? 0) < 10 && (
                  <TextButton
                    leftIcon={Plus}
                    onLinkClick={handleAddExtension}
                    variant={TextButtonVariants.Info}
                    textButtonProps={{ padding: '.5rem' }}
                    leftIconProps={{
                      boxSize: '12px',
                      position: 'relative',
                      bottom: '.5px',
                      stroke: 'inherit',
                      strokeWidth: '2px',
                    }}
                  >
                    {t('phoneSettings.addExtension')}
                  </TextButton>
                )}
              </Flex>

              <Flex
                id="speed-dial"
                width={'100%'}
                borderRadius={'0.25rem'}
                display={'flex'}
                flexDir={'column'}
                marginTop={'0.5rem'}
                alignItems={'flex-start'}
                gap={'0.25rem'}
              >
                <Text css={styles.text3} color="fg.default">
                  {t('phoneSettings.speedDial', {
                    maxSpeedDials: MAX_SPEED_DIALS,
                  })}
                </Text>

                {Array.from(speedDial).map(([key, value]) => (
                  <Flex key={key} css={styles.speedDialContainer}>
                    <Flex
                      display={'flex'}
                      flexDir={'column'}
                      paddingY={'0.75rem'}
                    >
                      <Text css={styles.text2} color="fg.default">
                        {value.label}
                      </Text>
                      <Text css={styles.text4} color="fg.default">
                        {value.number}
                      </Text>
                    </Flex>
                    <Spacer />

                    <QuickActionButton
                      variant={QuickActionButtonVariants.Default_100}
                      size={Size.sm}
                      actionIcon={Pencil}
                      tooltipText={t('phoneSettings.editSpeedDial')}
                      onClick={() => handleEditSpeedDial(value)}
                      iconProps={{ boxSize: '0.75rem' }}
                    />
                    <QuickActionButton
                      variant={QuickActionButtonVariants.Default_100}
                      size={Size.sm}
                      actionIcon={X}
                      tooltipText={t('phoneSettings.removeSpeedDial')}
                      onClick={() => removeSpeedDial(Number(key))}
                      iconProps={{ boxSize: '0.75rem' }}
                    />
                  </Flex>
                ))}
                {Array.from(speedDial).length <= MAX_SPEED_DIALS && (
                  <TextButton
                    leftIcon={Plus}
                    textButtonProps={{ padding: '.5rem' }}
                    onLinkClick={handleAddSpeedDial}
                    variant={TextButtonVariants.Info}
                    leftIconProps={{
                      boxSize: '12px',
                      position: 'relative',
                      bottom: '.5px',
                      stroke: 'inherit',
                    }}
                  >
                    {t('phoneSettings.addSpeedDial')}
                  </TextButton>
                )}
              </Flex>
            </SubheadingSection>
          </VStack>
        }
        toggleOffContent={
          <VStack css={styles.vStack2}>
            <SubheadingSection id="click-to-call">
              <Text css={styles.text1} color="fg.default">
                {t('phoneSettings.clickToCall')}
              </Text>
              <SettingToggle
                label={t('phoneSettings.autoAnswer')}
                isChecked={shouldAutoAnswer}
                onClick={handleAutoAnswerToggle}
              />
            </SubheadingSection>
          </VStack>
        }
      />
    </DefaultSettingsWrapper>
  )
}
